import React from "react";
import { motion } from "framer-motion";
import { ProblemSectionProps } from "@/types/components";

const DEFAULT_PROBLEMS = [
  {
    title: "Business Bleeding Revenue Daily",
    subtitle: "Every Delay Costs Millions",
    description: "While competitors process policies in hours, your manual workflows take weeks. Customer acquisition stalls, retention drops, and market share evaporates as business leaders watch helplessly from the sidelines."
  },
  {
    title: "IT Trapped in Implementation Hell",
    subtitle: "18-Month Projects, Zero Results",
    description: "Every 'solution' promises transformation but delivers complexity. Integration projects stretch for years, budgets explode, and IT teams burn out trying to make legacy systems talk to modern tools that never quite work as advertised."
  },
  {
    title: "Compliance Living in Constant Fear",
    subtitle: "Audit Failures Waiting to Happen",
    description: "Regulations change overnight, but your systems take months to adapt. Compliance teams scramble with manual processes, praying the next audit doesn't expose the gaps that could cost your license—and everyone's job."
  },
  {
    title: "Executive Frustration Boiling Over",
    subtitle: "Promises Made, Deadlines Missed",
    description: "Another quarter, another missed digital transformation milestone. Boards demand results, competitors gain ground, and the gap between what technology promises and what actually gets delivered grows wider every day."
  }
];

const ProblemSection: React.FC<ProblemSectionProps> = ({ problems = DEFAULT_PROBLEMS, className }) => {
  return (
    <section className="relative py-20 bg-gradient-to-b from-gray-900 to-gray-800">
      {/* Background overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.1),rgba(255,255,255,0))]" />
      
      <div className={`container mx-auto px-4 relative z-10 ${className || ''}`}>
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-emerald-400">
            Critical Challenges
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {problems.map((problem, index) => (
            <motion.div
              key={problem.title}
              className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <h3 className="text-lg md:text-xl font-semibold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-emerald-400">
                {problem.title}
              </h3>
              <p className="text-sm text-gray-500 mb-3">
                {problem.subtitle}
              </p>
              <p className="text-sm text-gray-400">
                {problem.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
